// SessionSharePage.jsx - Public view for a shared individual session.

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";

function SessionSharePage() {
  const { slug, sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const endpoint = slug
          ? `${API_BASE}/share/session/${slug}/${sessionId}`
          : `${API_BASE}/share/session/${sessionId}`;
        const response = await httpRequest(endpoint, { method: "GET" });
        if (response.ok) {
          setSession(await response.json());
        } else {
          setError("Session not found");
        }
      } catch (err) {
        setError("Error loading session");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, sessionId]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
          <div>Loading session…</div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🎲</div>
          <h2>Session not found</h2>
          <p style={{ color: "var(--text-secondary, #666)" }}>
            {error === "Session not found"
              ? "This shared session may have expired or is invalid."
              : error || "This shared session may have expired or is invalid."}
          </p>
        </div>
      </div>
    );
  }

  const winnerIds = new Set(session.winners || []);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--bg-primary, white)",
        padding: "1.5rem 1rem",
      }}
    >
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontWeight: 700,
            color: "var(--pista-button-bg, #3f51b5)",
            marginBottom: "1.5rem",
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
            }}
          />
          Pista by VoyagerBug
        </div>

        <div
          style={{
            backgroundColor: "var(--bg-secondary, #fafbfc)",
            border: "1px solid var(--border-color, #e0e0e0)",
            borderRadius: "16px",
            padding: "1.5rem",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}
        >
          {session.game_id && (
            <img
              src={`${API_BASE}/games/${session.game_id}/thumbnail`}
              alt={session.game_name}
              style={{
                width: "160px",
                height: "160px",
                objectFit: "cover",
                borderRadius: "12px",
                marginBottom: "1rem",
                backgroundColor: "#eee",
              }}
            />
          )}

          <h1 style={{ margin: "0 0 0.5rem", fontSize: "1.75rem" }}>{session.game_name}</h1>
          <p style={{ color: "var(--text-secondary, #666)", marginBottom: "1.25rem" }}>
            Played on {session.date_display || "a past date"}
          </p>

          {session.participants?.length > 0 && (
            <>
              <h3 style={{ fontSize: "1.1rem", margin: "1.5rem 0 0.5rem" }}>Players</h3>
              <ul style={{ paddingLeft: "1.25rem", margin: 0 }}>
                {session.participants.map((p, i) => {
                  const name = p.username || p.name || p.display_name || "Player";
                  const isWinner = winnerIds.has(p.user_id);
                  return (
                    <li key={i} style={{ marginBottom: "0.25rem" }}>
                      {isWinner ? "🏆 " : ""}
                      {name}
                      {p.score !== undefined && p.score !== "" ? ` — ${p.score}` : ""}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {session.notes && (
            <p style={{ marginTop: "1.25rem", color: "var(--text-secondary, #666)" }}>{session.notes}</p>
          )}

          <a
            href={session.share_url}
            style={{
              display: "inline-block",
              marginTop: "1.5rem",
              padding: "0.75rem 1.5rem",
              backgroundColor: "var(--pista-green-primary, #2E7D32)",
              color: "white",
              textDecoration: "none",
              borderRadius: "8px",
              fontWeight: 600,
            }}
          >
            Open in Pista
          </a>
        </div>
      </div>
    </div>
  );
}

export default SessionSharePage;
